import {useCallback, useEffect, useMemo, useState} from 'react';
import {getActiveIframe} from '@packages/systems/core/helpers/SiteIframeMigrationHelper';
import {selectDesignerAbility} from '@packages/systems/designer-abilities';
import {dismissNote} from '@packages/systems/designer/apps/utils';
import {useFeatureFlag} from '@packages/systems/feature-config/client';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {
  useDesignerSelector,
  useDispatch,
  type DesignerStores,
} from '@packages/systems/designer/flux';
import {SYMBOLS_PANEL} from '@packages/systems/designer/panels';
import {
  getIsDesignerCanvasWorkflow,
  getIsDesignSiteMode,
} from '@packages/systems/designer-selectors';
import {getActiveRoot} from '@packages/systems/panels';
import {useCodeComponentCreated} from './flux/CodeLibrariesSlice';
import type {CodeComponentTemplateId} from '@packages/systems/code-libraries/core';

export const CODE_COMPONENT_TEMPLATE_NUDGE = 'CODE_COMPONENT_TEMPLATE_NUDGE';

type TemplateNudge = {
  templateId: CodeComponentTemplateId;
  noteId: string;
  pattern: RegExp;
};

// Checked in order, so pricing wins "buy now" over the quotes carousel. Each
// nudge is dismissed on its own.
export const CODE_COMPONENT_TEMPLATE_NUDGES: readonly TemplateNudge[] = [
  {
    templateId: 'pricing-calculator',
    noteId: CODE_COMPONENT_TEMPLATE_NUDGE,
    pattern: /\b(?:pricing|buy\s+now)\b/i,
  },
  {
    templateId: 'multistep-form',
    noteId: `${CODE_COMPONENT_TEMPLATE_NUDGE}_MULTISTEP_FORM`,
    pattern: /\bsign[\s-]?up\b/i,
  },
  {
    templateId: 'locations-map',
    noteId: `${CODE_COMPONENT_TEMPLATE_NUDGE}_LOCATIONS_MAP`,
    pattern: /\b(?:locations?|find\s+us)\b/i,
  },
  {
    templateId: 'shader',
    noteId: `${CODE_COMPONENT_TEMPLATE_NUDGE}_SHADER`,
    pattern: /\b(?:mission|manifesto|about|early\s+access|careers)\b/i,
  },
  {
    templateId: 'quotes-carousel',
    noteId: `${CODE_COMPONENT_TEMPLATE_NUDGE}_QUOTES_CAROUSEL`,
    pattern: /\b(?:buy\s+now|free\s+trial)\b/i,
  },
];

const findNudge = (nudges: readonly TemplateNudge[], text: string) =>
  nudges.find(({pattern}) => pattern.test(text));

const canEditCodeComponentsSelector = selectDesignerAbility(
  'canEditCodeComponents'
);
const selectHasRenderedCanvas = (state: DesignerStores) =>
  state.CanvasBodyRenderStore.canvasBodyFirstTimeRenderHappened;
const selectPermanentlyDismissedNotes = (state: DesignerStores) =>
  state.DesignerStore.permanentlyDismissedNotes;

const selectCodeComponentTemplatePage = (state: DesignerStores) => {
  const page = state.PageStore.staticPages.get(
    state.DesignerStore.currentPageId
  );
  const activePanel = getActiveRoot(state.PanelsStore, 'left');

  return page &&
    canEditCodeComponentsSelector(state) &&
    getIsDesignerCanvasWorkflow(state.DesignerStore) &&
    getIsDesignSiteMode(state.DesignerStore) &&
    !state.UiStore.isPageLoading &&
    !state.UiStore.uiHidden &&
    !state.EmbedEditorUIStore.embedEditorShown &&
    (!activePanel || activePanel === SYMBOLS_PANEL)
    ? page
    : null;
};

export function useCodeComponentTemplateNudge() {
  const page = useDesignerSelector(selectCodeComponentTemplatePage);
  const hasRenderedCanvas = useDesignerSelector(selectHasRenderedCanvas);
  const dismissedNotes = useDesignerSelector(selectPermanentlyDismissedNotes);
  const dispatch = useDispatch();
  const areTemplatesEnabled = useFeatureFlag(
    FEATURE_FLAGS.AICC_COMPONENT_TEMPLATES
  );
  const pendingNudges = useMemo(
    () =>
      CODE_COMPONENT_TEMPLATE_NUDGES.filter(
        ({noteId}) => !dismissedNotes.has(noteId)
      ),
    [dismissedNotes]
  );
  // Once a nudge closes, its page offers no other template for the rest of
  // the session, so dismissing one never pops up the next.
  const [closedPageIds, setClosedPageIds] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  const eligiblePage =
    areTemplatesEnabled &&
    pendingNudges.length &&
    page &&
    !closedPageIds.has(page.id)
      ? page
      : null;
  const pageNameNudge = eligiblePage
    ? findNudge(pendingNudges, eligiblePage.name)
    : undefined;
  const pageIdToScan =
    eligiblePage && !pageNameNudge && hasRenderedCanvas
      ? eligiblePage.id
      : null;
  const [headingMatch, setHeadingMatch] = useState<{
    pageId: string;
    nudge: TemplateNudge;
  } | null>(null);

  useEffect(() => {
    // Scan once when the page becomes eligible, not on edits or unrelated renders.
    const headings = pageIdToScan
      ? getActiveIframe()?.contentDocument?.querySelectorAll(
          'h1, h2, h3, h4, h5, h6'
        )
      : undefined;
    let nudge: TemplateNudge | undefined;
    for (const heading of Array.from(headings ?? [])) {
      nudge = findNudge(pendingNudges, heading.textContent ?? '');
      if (nudge) break;
    }
    setHeadingMatch(
      pageIdToScan && nudge ? {pageId: pageIdToScan, nudge} : null
    );
  }, [pageIdToScan, pendingNudges]);

  const nudge =
    pageNameNudge ??
    (eligiblePage && headingMatch?.pageId === eligiblePage.id
      ? headingMatch.nudge
      : undefined);
  const createCodeComponent = useCodeComponentCreated();
  const onOpenChange = useCallback(
    (isOpen: boolean) => {
      if (isOpen || !nudge || !eligiblePage) return;
      dispatch(dismissNote({id: nudge.noteId}));
      setClosedPageIds((ids) => new Set(ids).add(eligiblePage.id));
    },
    [dispatch, eligiblePage, nudge]
  );
  const onCreate = useCallback(
    (templateId?: CodeComponentTemplateId) => {
      createCodeComponent({templateId});
    },
    [createCodeComponent]
  );

  return {
    isOpen: Boolean(nudge),
    templateId: nudge?.templateId,
    onOpenChange,
    onCreate,
  };
}
